export const INDIVIDUAL = "00000000-0000-0000-0000-000000000000";
export const EXTERNAL = "10000000-0000-0000-0000-000000000000";

function id(value) {
  if (value === null || value === undefined || String(value).trim() === "") throw new Error("Missing record identity");
  return String(value);
}
function number(value, name) {
  if (value === null || value === undefined || value === "" || !Number.isFinite(Number(value))) throw new Error("Invalid numeric field: " + name);
  return Number(value);
}
function records(value, name, fields = [], times = []) {
  if (!Array.isArray(value)) throw new Error("Invalid array: " + name);
  return value.map(row => {
    if (!row || typeof row !== "object" || Array.isArray(row)) throw new Error("Invalid record: " + name);
    const result = { ...row, id: id(row.id) };
    fields.forEach(field => { result[field] = row[field] == null ? null : id(row[field]); });
    times.forEach(field => { result[field] = row[field] == null ? null : number(row[field], field); });
    if (times.includes("start") && result.start === null) throw new Error("Missing start: " + name);
    delete result.taken;
    return result;
  });
}
export function unique(records) {
  return Array.from(new Map(records.map(record => [record.id, record])).values());
}
const flag = value => value === true || value === 1 || value === "1";
export function normalizeSnapshot(data) {
  if (!Array.isArray(data.options) || !data.options[0]) throw new Error("Missing Pausenplan options");
  const options = { ...data.options[0] };
  options.timeRange = number(options.timeRange, "timeRange");
  if (options.timeRange <= 0 || options.timeRange > 168) throw new Error("Invalid timeRange");
  const external = data.external;
  if (!external || typeof external !== "object") throw new Error("Invalid external snapshot");
  const positions = unique(records(data.positions, "positions", ["md_id"]).map(position => {
    if (!position.md_id) throw new Error("Missing position identity");
    return { ...position, active: flag(position.active) };
  }));
  const externalPositions = records(external.positions, "external positions", ["md_id"]);
  const internalRotas = unique(records(data.rotas, "rotas", ["employee_id", "spot_id", "externalStoreId"], ["start", "end", "planPauseStart", "planPauseDuration"]));
  if (internalRotas.some(rota => rota.end === null)) throw new Error("Missing rota end");
  const hiddenRotas = internalRotas.filter(rota => rota.externalStoreId !== null);
  const incoming = records(external.rotas, "external rotas", ["employee_id", "spot_id"], ["start", "end", "planPauseStart", "planPauseDuration"]).map(rota => {
    if (rota.end === null) throw new Error("Missing external rota end");
    const originalPosition = externalPositions.find(position => position.md_id === rota.spot_id);
    const match = originalPosition && positions.find(position => position.name === originalPosition.name);
    return { ...rota, spot_id: match ? match.md_id : rota.spot_id, extern: true };
  });
  const hiddenIds = new Set(hiddenRotas.map(rota => rota.id));
  // Internal records win metadata conflicts, independently of response timing.
  const rotas = unique([...incoming, ...internalRotas.filter(rota => rota.externalStoreId === null).map(rota => ({ ...rota, extern: false }))]);
  const shifts = unique([
    ...records(external.shifts, "external shifts", ["employee_id", "id_rota"], ["start", "end"]).map(shift => ({ ...shift, extern: true })),
    ...records(data.shifts, "shifts", ["employee_id", "id_rota"], ["start", "end"]).filter(shift => !hiddenIds.has(shift.id_rota)).map(shift => ({ ...shift, extern: false }))
  ]);
  const employees = unique([
    ...records(external.employees, "external employees").map(employee => ({ ...employee, extern: true })),
    ...records(data.employees, "employees").map(employee => ({ ...employee, extern: false }))
  ]);
  if ([...rotas, ...shifts].some(record => !record.employee_id)) throw new Error("Missing employee reference");
  return { options, positions, employees, rotas, shifts, hiddenRotas, pauses: [] };
}
export function normalizePauses(data) {
  const pauses = unique(records(data, "pauses", ["id_shift"], ["start", "end"]));
  // The legacy endpoint may return an unfinished pause with a null end.
  if (pauses.some(pause => !pause.id_shift)) throw new Error("Invalid pause");
  return pauses;
}
export function optionState(options) {
  return {
    showKPIs: flag(options.showKPIs), timeframe: options.timeRange,
    showFinished: flag(options.showCompletedShifts), showRestingArea: flag(options.showRestingArea),
    hideFinishedWhichAreOlderThanHours: flag(options.showCompletedShifts) ? Number(options.hideCompletedShiftsAfterHours) || 0 : 0,
    showFuture: flag(options.showFutureShifts) ? Number(options.showFutureShiftsWithinHours) || 0 : 0.1,
    actualShiftEndedOpacity: Number(options.visibilityCompletedShifts) / 100,
    actualShiftNotStartedOpacity: Number(options.visibilityFutureShifts) / 100,
    showHourlyDivisions: flag(options.showHelpBarsUnderShifts), usePositions: flag(options.usePositions),
    showDefaultPause: flag(options.showDefaultPause), countryId: Number(options.countryId),
    spotActualShiftColorEnabled: flag(options.usePositions) && flag(options.colorizeLayers),
    spotPlanShiftColorEnabled: flag(options.usePositions) && flag(options.colorizeLayers),
    pausenplanIdentifier: options.number, customPositions: Array.isArray(options.positions) ? options.positions : []
  };
}
export function displayPositions(snapshot, translate) {
  const options = optionState(snapshot.options);
  const custom = new Map(options.customPositions.map(position => [String(position.id), position]));
  const positions = options.usePositions ? snapshot.positions.map(position => {
    const setting = custom.get(position.id);
    return { ...position, ...(setting ? { sort: setting.sort, active: flag(setting.active) } : {}), employees: [] };
  }) : [];
  positions.push(
    { id: "synthetic:individual", md_id: INDIVIDUAL, name: options.usePositions ? translate("positions.individual") : "", active: true, color: "#777777", sort: 9998, employees: [] },
    { id: "synthetic:external", md_id: EXTERNAL, name: translate("positions.external"), active: true, color: "#bd6c17", sort: 9999, employees: [] }
  );
  return positions.sort((a, b) => (Number(a.sort) || 0) - (Number(b.sort) || 0));
}
export function currentPauses(positions, timestamp, maxCards) {
  const found = new Map();
  positions.filter(position => position.active).forEach(position => position.employees.forEach(employee => {
    const pause = employee.pauses.find(p => p.start < timestamp && p.start + p.duration > timestamp);
    if (pause && !found.has(pause.id)) found.set(pause.id, { employee, pause });
  }));
  return Array.from(found.values()).sort((a, b) => a.pause.end - b.pause.end).slice(0, Math.max(0, maxCards));
}
